import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import ColorDot from './ColorDot';

/**
 * Product card's image + color swatch dots + swipeable photo gallery, bundled
 * together since they share state. Drop this in wherever a card currently renders its
 * own `<Link><img/></Link>` block - everything else about the card (title, price,
 * rating, badges) stays exactly as it was.
 *
 * `product.colors` and `product.gallery_images` come from GET /api/products (see
 * products-simple.js): colors is {value, image_url, in_stock}[] built from that
 * product's variants (empty unless it has a "Color" option); gallery_images is the
 * product's own full photo set from product_images, independent of color.
 *
 * A selected color's own photo always takes precedence over the base gallery (existing
 * swatch behavior). Swiping through the base gallery only applies when no such color
 * override is active - a color's photo is a single specific picture, not itself a
 * swipeable set (yet - see the note in AdminSettings/ProductVariantsManager about
 * multi-image-per-variant if that's wanted later).
 */
export default function ProductMediaWithSwatches({
  product,
  linkClassName,
  containerClassName,
  imgClassName,
  placeholderSrc = '/api/placeholder/400/300',
  children // optional overlay content (badges, wishlist icon, etc.) rendered inside the link
}) {
  const colors = product.colors || [];
  const [selectedColorIdx, setSelectedColorIdx] = useState(0);
  const [photoIdx, setPhotoIdx] = useState(0);
  const touchStartX = useRef(null);
  const draggedRef = useRef(false);

  const selectedColorImage =
    colors.length > 1 && colors[selectedColorIdx] ? colors[selectedColorIdx].image_url : null;
  const gallery =
    product.gallery_images && product.gallery_images.length > 0
      ? product.gallery_images
      : product.image || product.image_url
      ? [product.image || product.image_url]
      : [];
  const canSwipeGallery = !selectedColorImage && gallery.length > 1;
  const displaySrc = selectedColorImage || gallery[photoIdx] || product.image || product.image_url || placeholderSrc;

  const onTouchStart = (e) => {
    if (!canSwipeGallery) return;
    touchStartX.current = e.touches[0].clientX;
    draggedRef.current = false;
  };
  const onTouchMove = (e) => {
    if (!canSwipeGallery || touchStartX.current === null) return;
    if (Math.abs(e.touches[0].clientX - touchStartX.current) > 10) draggedRef.current = true;
  };
  const onTouchEnd = (e) => {
    if (!canSwipeGallery || touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 30) {
      setPhotoIdx((prev) => (delta < 0 ? (prev + 1) % gallery.length : (prev - 1 + gallery.length) % gallery.length));
    }
    touchStartX.current = null;
  };
  // A swipe shouldn't also navigate to the product page - only a plain tap should.
  const onClickCapture = (e) => {
    if (draggedRef.current) {
      e.preventDefault();
      draggedRef.current = false;
    }
  };

  return (
    <div>
      <Link
        to={`/products/${product.id}`}
        className={linkClassName}
        onClickCapture={onClickCapture}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div className={`relative ${containerClassName || ''}`}>
          <img
            src={displaySrc}
            alt={product.name}
            className={imgClassName}
            loading="lazy"
            onError={(e) => {
              e.target.src = placeholderSrc;
            }}
          />
          {canSwipeGallery && (
            <div className="absolute bottom-1.5 left-0 right-0 flex items-center justify-center gap-1 pointer-events-none">
              {gallery.map((_, i) => (
                <span
                  key={i}
                  className={`h-1 rounded-full transition-all ${i === photoIdx ? 'w-3 bg-white' : 'w-1 bg-white/60'}`}
                  style={{ boxShadow: '0 0 2px rgba(0,0,0,0.4)' }}
                />
              ))}
            </div>
          )}
        </div>
        {children}
      </Link>

      {colors.length > 1 && (
        <div className="flex items-center gap-1.5 px-2.5 pt-1.5" role="group" aria-label="Available colors">
          {colors.map((c, i) => (
            <ColorDot
              key={c.value}
              value={c.value}
              size={16}
              selected={i === selectedColorIdx}
              disabled={c.in_stock === false}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSelectedColorIdx(i);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
